import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SlotCandidatesLeftSidebar from "./Sidebars/SlotCandidatesLeftSidebar";
import CandidateRightSidebar from "./Sidebars/CandidateRightSidebar";
import SlotCandidateRow, { isCandidateSelectable } from "./SlotCandidateRow";
import Notification from "../Shared/Notification";
import {  getAssessmentSlotStatus, updateAssessmentSlotStatus, bulkResetCandidateWindows, bulkSendAssessmentLinkWithoutSeb } from "../../api/assessmentslots.api";
import { updateAssessmentDetailsById } from "../../api/assessmentquestionnaires.api";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import CandidateFunnelBar, { FunnelFilter, isOngoingNow } from "./CandidateFunnelBar";

import { getCandidatesBySlot, updateBulkCandidateAssessmentSlotStatus } from "../../api/candidates.api";
import useApiToken from "../../hooks/useApiToken";
import { useSlotWindowResetAlerts } from "../../hooks/useSlotWindowResetAlerts";
import { useRefreshLimitPauseAlerts } from "../../hooks/useRefreshLimitPauseAlerts";

interface SlotInfo {
  slotId: string;
  roleId: string;
  roleStatusId: string;
  assessmentName: string;
  roleName: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  assessmentDuration: number; // in minutes
}

interface SlotCandidate {
  candidateId: string;
  candidateName: string;
  candidateEmail: string;
  hasBooked: boolean;
  status: string;
  score: number | null;
  startTime: string | null;
  endTime: string | null;
  adminStatus: number; // -1=cancelled, 0=paused, 1=started
  hasPendingWindowReset: boolean;
  isPausedForRefreshLimit: boolean;
  usedSeb: boolean;
  bypassedSeb: boolean;
  takenWithoutSeb: boolean;
}

const SlotCandidates: React.FC = () => {
  const { slotId } = useParams<{ slotId: string }>();
  const navigate = useNavigate();
  const token = useApiToken();
  const [isLeftOpen, setIsLeftOpen] = useState<boolean>(true);
  const [slotInfo, setSlotInfo] = useState<SlotInfo | null>(null);
  const [candidates, setCandidates] = useState<SlotCandidate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [sidebarFilters, setSidebarFilters] = useState<Record<string, string[]>>({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [adminOptionFlag , setAdminOptionFlag] = useState<number>(0);
  // Funnel bar: client-side filter — does NOT trigger a new API call
  const [funnelFilter, setFunnelFilter] = useState<FunnelFilter>("all");
  const [isSendingWithoutSeb, setIsSendingWithoutSeb] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationContent, setNotificationContent] = useState<{
    title: string;
    description: string;
    type: "success" | "error" | "info";
  }>({ title: "", description: "", type: "success" });

  const notify = (
    title: string,
    description: string,
    type: "success" | "error" | "info"
  ) => {
    setNotificationContent({ title, description, type });
    setShowNotification(true);
  };

  const {
    getAlertForCandidate,
    broadcastWindowResetGranted,
    broadcastWindowResetDismissed,
  } = useSlotWindowResetAlerts(slotId);

  // Separate hook and separate connection from the window-reset alerts above, matching the
  // existing pattern. Consolidating the two is worthwhile but is its own change.
  const {
    getPauseAlertForCandidate,
    broadcastPauseResolved,
  } = useRefreshLimitPauseAlerts(slotId);

  // Fetch candidates from backend. Runs only on mount or manual actions.
  const fetchCandidatesBySlot = useCallback(async () => {
    if (!slotId) return;
    try {
      setLoading(true);
      const data = await getCandidatesBySlot(slotId, "", "", token!);
      setSlotInfo(data.slotInfo);
      setCandidates(data.candidates);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      setLoading(false);
    }
  }, [slotId, token]);

  // Client-side filtering
  const filteredCandidates = (() => {
    let result = candidates;

    // 1. Funnel Filter
    switch (funnelFilter) {
      case "booked":
        result = result.filter((c) => c.hasBooked);
        break;
      case "started":
        result = result.filter((c) => c.startTime !== null);
        break;
      case "ongoing":
        result = result.filter((c) =>
          isOngoingNow(c, slotInfo?.assessmentDuration ?? 0)
        );
        break;
      case "completed":
        result = result.filter((c) => c.endTime !== null && c.score !== null);
        break;
      case "not_booked":
        result = result.filter((c) => !c.hasBooked);
        break;
      case "not_started":
        result = result.filter((c) => c.hasBooked && c.startTime === null);
        break;
    }

    // 2. Search Query — comma / newline separated terms are OR-matched, so a recruiter
    //    can paste a whole list of names ("Aman, Riya Sharma, karan@x.com") and act on
    //    all of them in one selection.
    const searchTerms = searchQuery
      .split(/[,\n]/)
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t !== "");

    if (searchTerms.length > 0) {
      result = result.filter((c) => {
        const name = c.candidateName.toLowerCase();
        const email = c.candidateEmail.toLowerCase();
        return searchTerms.some((t) => name.includes(t) || email.includes(t));
      });
    }

    // 3. Status Filter
    if (sidebarFilters.Status && !sidebarFilters.Status.includes("all")) {
      result = result.filter((c) => sidebarFilters.Status.includes(c.status.replace(" ", "")));
    }

    // 4. AdminStatus Filter
    if (sidebarFilters.AdminStatus && !sidebarFilters.AdminStatus.includes("all")) {
      const adminStatusMap: Record<string, number> = {
        Started: 1,
        Paused: 0,
        Cancelled: -1,
      };
      result = result.filter((c) => sidebarFilters.AdminStatus.some((s) => adminStatusMap[s] === c.adminStatus));
    }

    // 5. Alerts Filter
    if (sidebarFilters.Alerts && !sidebarFilters.Alerts.includes("all")) {
      // OR across the selected alert kinds, so picking both shows candidates matching either.
      const wantsReset = sidebarFilters.Alerts.includes("Reset Requested");
      const wantsPaused = sidebarFilters.Alerts.includes("Paused for Review");
      if (wantsReset || wantsPaused) {
        result = result.filter(
          (c) =>
            (wantsReset && c.hasPendingWindowReset) ||
            (wantsPaused && c.isPausedForRefreshLimit)
        );
      }
    }

    return result;
  })();

  // Select-all operates on the rows the recruiter can actually see, so combining the
  // search box / funnel / sidebar filters with select-all never touches hidden rows.
  const selectableVisibleIds = filteredCandidates
    .filter(isCandidateSelectable)
    .map((c) => c.candidateId);
  const allVisibleSelected =
    selectableVisibleIds.length > 0 &&
    selectableVisibleIds.every((id) => selectedCandidates.includes(id));
  const someVisibleSelected = selectableVisibleIds.some((id) =>
    selectedCandidates.includes(id)
  );

  useEffect(() => {
    fetchCandidatesBySlot();
  }, [fetchCandidatesBySlot]);

  const handleFiltersChange = (filters: Record<string, string[]>) => {
    setSidebarFilters(filters);
  };

  const handleSearchQuery = (query: string) => {
    setSearchQuery(query);
  };

  const handleSelectCandidate = (id: string) => {
    setSelectedCandidates((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((candidateId) => candidateId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };
  const handleAdminOptionClick = async (option: number) => {
    if (option === 1 || option === -1 || option === 0) {
      let actionName = "";
      if (option === 1) actionName = "start";
      else if (option === 0) actionName = "pause";
      else if (option === -1) actionName = "cancel";

      if (option === 0 || option === -1) {
        const targetText = selectedCandidates.length > 0 ? "the selected candidates" : "all candidates in this slot";
        if (!window.confirm(`Are you sure you want to ${actionName} the assessment for ${targetText}?`)) {
          return;
        }
      }

      setLoading(true);
      try {
        if (selectedCandidates.length > 0) {
          console.log("Bulk update:", selectedCandidates.length, "candidates, status:", option);
          await updateBulkCandidateAssessmentSlotStatus(
            selectedCandidates,
            slotId || "",
            option,
            token!
          );
          setSelectedCandidates([]);
        } else {
          console.log("Updating all candidates in slot, status:", option);
          await updateAssessmentSlotStatus(slotId || "", option, token!);
          setAdminOptionFlag(option);
        }
        fetchCandidatesBySlot();
      } catch (error) {
        console.error("Failed to update assessment slot status:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkResetWindows = async () => {
    if (!slotId || !token) return;
    if (selectedCandidates.length === 0) return;

    // Snapshot the ids before the selection is cleared below — they are needed to
    // broadcast the granted-reset event for each candidate.
    const resetCandidateIds = [...selectedCandidates];
    const count = resetCandidateIds.length;
    try {
      setLoading(true);
      await bulkResetCandidateWindows(resetCandidateIds, slotId, token);

      // The bulk endpoint only clears the DB flag (hasPendingWindowReset). The bell also
      // renders off the in-memory alert Map / localStorage entry, so without this broadcast
      // the badge stays on screen — and survives a refresh on every recruiter's PC.
      // Mirrors what the single-candidate popover path already does via onWindowResetGranted.
      // Awaited before the refetch so the alert cleanup can't race it.
      try {
        await Promise.all(
          resetCandidateIds.map((candidateId) =>
            broadcastWindowResetGranted(candidateId, slotId)
          )
        );
      } catch (broadcastError) {
        // The reset itself already succeeded — a failed broadcast must not surface as
        // "Reset Failed". broadcastWindowResetGranted falls back to local-only cleanup
        // when the hub is disconnected, so this PC's badge clears regardless.
        console.warn("Window reset succeeded but broadcast failed:", broadcastError);
      }

      setSelectedCandidates([]);
      notify(
        "Windows Reset",
        `Assessment window reset for ${count} candidate${count === 1 ? "" : "s"}.`,
        "success"
      );
      fetchCandidatesBySlot();
    } catch (error) {
      console.error("Failed to bulk reset candidate windows:", error);
      notify("Reset Failed", "Could not reset the assessment windows. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSendWithoutSeb = async () => {
    if (!slotId || !token) return;
    if (selectedCandidates.length === 0) return;

    try {
      setIsSendingWithoutSeb(true);
      const result = await bulkSendAssessmentLinkWithoutSeb(selectedCandidates, slotId, token);
      setSelectedCandidates([]);
      if (result.failed > 0) {
        const names = result.failedCandidates.map((c) => c.name || c.email).join(", ");
        notify(
          "Partially Sent",
          `${result.sent} link${result.sent === 1 ? "" : "s"} sent. Failed for: ${names}`,
          "error"
        );
      } else {
        notify(
          "Links Sent",
          `Non-SEB assessment link sent to ${result.sent} candidate${result.sent === 1 ? "" : "s"}.`,
          "success"
        );
      }
      fetchCandidatesBySlot();
    } catch (error) {
      console.error("Failed to bulk send assessment links without SEB:", error);
      notify("Send Failed", "Could not send the assessment links. Please try again.", "error");
    } finally {
      setIsSendingWithoutSeb(false);
    }
  };
 


  
  return (
    <div className="fixed inset-x-0 bottom-0 top-[4.1rem] z-0 overflow-hidden bg-gray-50 dark:bg-darkbg text-gray-900 dark:text-gray-100 flex flex-col">
      {/* Full-width sticky header above everything */}
      <header className="shrink-0 z-20 w-full bg-white dark:bg-darkbglight border-b border-gray-200 dark:border-gray-700 px-6 py-3 shadow-sm h-[4rem]">
        <div className="flex items-center gap-3 h-full">
          <button
            onClick={() => navigate("/tests")}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
          <div>
            <h2 className="text-lg font-semibold leading-tight">
              {slotInfo?.assessmentName || "Loading..."}
            </h2>
            {slotInfo && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {slotInfo.roleName} |{" "}
                {(() => {
                  const dateStr = slotInfo.startTime;
                  const hasExplicitTz = /([zZ]|[+\-]\d{2}:?\d{2})$/.test(dateStr);
                  const utcString = hasExplicitTz ? dateStr : `${dateStr}Z`;
                  const date = new Date(utcString);
                  return isNaN(date.getTime())
                    ? "Invalid date"
                    : date.toLocaleString();
                })()}
              </p>
            )}
          </div>
        </div>
      </header>

      <SlotCandidatesLeftSidebar
        onToggle={setIsLeftOpen}
        handleFiltersChange={handleFiltersChange}
        handleSearchQuery={handleSearchQuery}
      />
      <CandidateRightSidebar 
      adminOptionFlag = {adminOptionFlag}
      handleAdminOptionClick = {handleAdminOptionClick}/>

      <main
        className={`w-full flex-1 overflow-hidden flex flex-col mx-4 pt-4 pb-4 px-4 transition-[padding] duration-200 ease-in-out ${
          isLeftOpen ? "lg:pl-[19rem]" : "lg:pl-6"
        }`}
      >
        <div className="bg-white dark:bg-darkbglight rounded-md shadow-md border border-gray-200 dark:border-gray-700 p-4 flex flex-col flex-1 min-h-0">
          {/* Funnel summary bar — always shown once we have data */}
          {!loading && candidates.length > 0 && (
            <div className="shrink-0">
              <CandidateFunnelBar
                candidates={candidates}
                activeFilter={funnelFilter}
                onFilterChange={setFunnelFilter}
                assessmentDuration={slotInfo?.assessmentDuration ?? 0}
              />
            </div>
          )}

          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">Loading candidates...</p>
          ) : filteredCandidates.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
              {funnelFilter !== "all"
                ? `No candidates match the "${funnelFilter.replace("_", " ")}" filter.`
                : "No candidates found for the current filters."}
            </p>
          ) : (
            <>
            
              <div className="relative grid grid-cols-[auto_2fr_1fr_1fr_1fr_1fr_auto] gap-4 px-4 sm:px-6 lg:px-8 py-3 mt-4 border-b border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-darkbg rounded-t-md items-center shrink-0">
              {selectedCandidates.length > 0 && (
                  <div className="absolute inset-0 flex items-center gap-3 px-4 sm:px-6 lg:px-8 bg-indigo-50 dark:bg-indigo-900/30 rounded-t-md">
                    <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                      {selectedCandidates.length} selected
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedCandidates([])}
                      className="inline-flex items-center rounded-md bg-gray-700 dark:bg-gray-800 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 dark:hover:bg-gray-700"
                    >
                      Unselect
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAdminOptionClick(1)}
                      className="inline-flex items-center rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                    >
                      Start
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAdminOptionClick(0)}
                      className="inline-flex items-center rounded-md bg-yellow-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-yellow-400"
                    >
                      Pause
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAdminOptionClick(-1)}
                      className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkResetWindows}
                      className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                    >
                      Reset Window
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkSendWithoutSeb}
                      disabled={isSendingWithoutSeb}
                      title="Email the plain assessment link (no Safe Exam Browser required) to the selected candidates"
                      className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSendingWithoutSeb ? "Sending..." : "Resend Link (No SEB)"}
                    </button>
                  </div>
                )}
                <div className="w-4 flex items-center justify-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={selectableVisibleIds.length === 0}
                    title="Select all candidates currently visible"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCandidates((prev) =>
                          Array.from(new Set([...prev, ...selectableVisibleIds]))
                        );
                      } else {
                        setSelectedCandidates((prev) =>
                          prev.filter((id) => !selectableVisibleIds.includes(id))
                        );
                      }
                    }}
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                    }}
                  />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Name</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Score</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Start Time</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Time Remaining</span>
                </div>
                <div className="text-center">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Browser</span>
                </div>
                <div className="flex items-center justify-end gap-3">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Status</span>
                  <div className="w-8"></div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0">
                <ul className="space-y-0">
                  {filteredCandidates.map((candidate) => (
                    <SlotCandidateRow
                      key={candidate.candidateId}
                      candidate={candidate}
                      roleId={slotInfo?.roleId}
                      slotId={slotInfo?.slotId}
                      roleStatusId={slotInfo?.roleStatusId}
                      assessmentDuration={slotInfo?.assessmentDuration ?? 0}
                      assessmentName={slotInfo?.assessmentName}
                      isSelected={selectedCandidates.includes(candidate.candidateId)}
                      handleSelectCandidate={handleSelectCandidate}
                      onStatusChange={fetchCandidatesBySlot}
                      windowResetAlert={getAlertForCandidate(candidate.candidateId)}
                      onWindowResetGranted={broadcastWindowResetGranted}
                      onWindowResetDismissed={broadcastWindowResetDismissed}
                      refreshLimitPauseAlert={getPauseAlertForCandidate(candidate.candidateId)}
                      onRefreshLimitPauseResolved={broadcastPauseResolved}
                    />
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </main>

      <Notification
        show={showNotification}
        setShow={setShowNotification}
        title={notificationContent.title}
        description={notificationContent.description}
        type={notificationContent.type}
      />
    </div>
  );
};

export default SlotCandidates;
